import { FC, useContext, useEffect, useState } from 'react';
import { isEqual, pickBy } from 'lodash-es';
import { AiFillStar, AiOutlineStar } from 'react-icons/ai';
import { BiCaretDown } from 'react-icons/bi';
import {
  Dialog,
  Flex,
  Group,
  Icon,
  IconButton,
  Menu,
  Portal,
  Text,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { SortDirection } from '@tanstack/react-table';

import { IconTrash } from '~/core/shared/assets';
import { useUserData } from '~/core/shared/contexts/UserContext';
import { DocumentFilter, Nullable } from '~/core/shared/types';
import { handleSuccess } from '~/core/shared/utils/admin';
import { camelToSnakeCase } from '~/core/shared/utils/general';
import { useDeleteFilterQuery } from '../../hooks/useDeleteFilterQuery';
import { DocumentListContext } from '../application';
import { Button } from '../Button.component';

const SaveFilterMenuButton = ({ ...props }) => (
  <Menu.Trigger asChild>
    <IconButton
      borderColor="black"
      color="black"
      colorPalette="white"
      lineHeight={0}
      size="xs"
      variant="outline"
      {...props}
    >
      <BiCaretDown />
    </IconButton>
  </Menu.Trigger>
);

export const SaveFilterButton: FC = () => {
  const { user } = useUserData();
  const {
    saveFilterModalApi,
    savedFilters,
    setStateFilters,
    setSorting,
    chosenFilter,
    setChosenFilter,
    filters,
    sorting,
  } = useContext(DocumentListContext);
  const [deletedFilter, setDeletedFilter] =
    useState<Nullable<DocumentFilter>>(null);
  const onCloseDelete = (): void => setDeletedFilter(null);
  const queryClient = useQueryClient();

  const onChooseFilter = (
    filter: DocumentFilter,
    hideSuccessMsg?: boolean,
  ): void => {
    setChosenFilter(filter);
    const { sortcolumn, sortdirection, ...filterParameters } =
      filter.parameters;
    setStateFilters(filterParameters);

    if (sortcolumn && sortdirection) {
      setSorting({
        column: sortcolumn,
        direction: sortdirection as SortDirection,
      });
    }

    if (!hideSuccessMsg) {
      handleSuccess('Search applied');
    }
  };

  useEffect(() => {
    if (user?.documentFilter) {
      const defaultFilter = savedFilters?.find(
        (x) => x.id === (user.documentFilter as any).id,
      );

      if (defaultFilter) {
        onChooseFilter(defaultFilter, true);
      }
    }
  }, [user?.documentFilter]);

  const onSuccess = (): void => {
    setDeletedFilter(null);
    handleSuccess('Search deleted');
    queryClient.invalidateQueries({ queryKey: ['filters'] });
  };

  const { deleteFilter } = useDeleteFilterQuery(onSuccess);

  const onDelete = async (): Promise<void> => {
    if (deletedFilter) {
      await deleteFilter(deletedFilter.id);
    }
  };

  useEffect(() => {
    if (chosenFilter) {
      const filter = {
        ...filters,
        sortcolumn: camelToSnakeCase(sorting.column),
        sortdirection: sorting.direction,
      };

      if (!isEqual(pickBy(filter), pickBy(chosenFilter.parameters))) {
        setChosenFilter(null);
      }
    }
  }, [filters, sorting]);

  return (
    <>
      <Menu.Root>
        <Group attached>
          <Button
            borderColor="black"
            color="black"
            colorPalette="white"
            fontSize="sm"
            fontWeight="bold"
            onClick={saveFilterModalApi.onOpen}
            size="xs"
            variant="outline"
            w="fit-content"
          >
            {chosenFilter ? (
              <Icon as={AiFillStar} color="yellow.500" />
            ) : (
              <Icon as={AiOutlineStar} />
            )}
            {chosenFilter ? chosenFilter.name : 'Save'}
          </Button>
          <SaveFilterMenuButton />
        </Group>

        <Portal>
          <Menu.Positioner>
            <Menu.Content py={0}>
              <Menu.ItemGroup>
                <Menu.ItemGroupLabel>Saved searches</Menu.ItemGroupLabel>
                {savedFilters && savedFilters.length > 0 ? (
                  savedFilters.map((savedFilter) => (
                    <Menu.Item
                      key={savedFilter.id}
                      alignItems="center"
                      as={Flex}
                      borderTop="1px solid"
                      borderTopColor="gray.200"
                      color="black"
                      justifyContent="space-between"
                      value={savedFilter.name}
                      w="full"
                    >
                      <Text
                        _hover={{ fontWeight: 'bold' }}
                        cursor="pointer"
                        fontWeight={
                          savedFilter.id === chosenFilter?.id
                            ? 'bold'
                            : 'normal'
                        }
                        onClick={() => onChooseFilter(savedFilter)}
                      >
                        {savedFilter.name}
                      </Text>
                      <Icon
                        _hover={{ boxSize: '22px' }}
                        as={IconTrash}
                        boxSize="20px"
                        cursor="pointer"
                        onClick={() => setDeletedFilter(savedFilter)}
                      />
                    </Menu.Item>
                  ))
                ) : (
                  <Menu.Item value="no-saved-searches">
                    No saved searches
                  </Menu.Item>
                )}
              </Menu.ItemGroup>
            </Menu.Content>
          </Menu.Positioner>
        </Portal>
      </Menu.Root>

      <Dialog.Root
        onOpenChange={onCloseDelete}
        open={deletedFilter !== null}
        role="alertdialog"
      >
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header fontSize="lg" fontWeight="bold">
              Delete Search
            </Dialog.Header>

            <Dialog.Body pl={6}>
              Are you sure to delete the <b>{deletedFilter?.name}</b> search?
              You can‘t undo this action afterwards.
            </Dialog.Body>

            <Dialog.Footer>
              <Dialog.ActionTrigger asChild>
                <Button colorPalette="gray" onClick={onCloseDelete}>
                  Cancel
                </Button>
              </Dialog.ActionTrigger>
              <Button
                colorPalette="primary"
                ml={3}
                onClick={onDelete}
                variant="solid"
              >
                Delete
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>
    </>
  );
};
